import { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, CameraOff } from "lucide-react";

// HIS STANDING RULE: carts, pulls, shipping and transfers are NEVER held up by a missing tag or photo.
// The alert comes first, then the logged bypass: pick why, add a note if needed — who and when are logged.
export const BYPASS_REASONS = ["Photo later — part needed now", "Camera or phone not working",
  "Rush pull for the job", "Photo taken, not uploaded yet", "Other"];

const slug = (r) => r.toLowerCase().replace(/[^a-z]+/g, "-").slice(0, 24).replace(/^-|-$/g, "");

export function gapWords(missing) {
  const m = (missing || []).join(" ");
  return ["tag", "photo"].filter((w) => m.includes(w)).join(" and ") || "photo";
}

function headline(block) {
  const parts = block?.parts || [];
  if (parts.length > 1) return `${parts.length} parts have no photo or tag yet: ${parts.map((p) => p.short_code).join(", ")}`;
  const one = parts[0] || block || {};
  return `${one.short_code || "This part"} has no ${gapWords(one.missing)} yet`;
}

export function NoPhotoBypassPanel({ block, after, actionLabel, onConfirm, onCancel, busy, big }) {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const needsNote = reason === "Other";
  const ready = !!reason && (!needsNote || note.trim().length >= 3);
  const txt = big ? "text-lg" : "text-base";
  return (
    <div data-testid="no-photo-bypass" className="space-y-4">
      <div className="flex items-start gap-3 rounded-xl border-2 border-amber-400 bg-amber-50 p-4">
        <CameraOff className="w-7 h-7 shrink-0 text-amber-700" />
        <div>
          <div data-testid="no-photo-title" className={`font-black text-amber-950 ${big ? "text-2xl" : "text-lg"}`}>{headline(block)}</div>
          <div className={`mt-1 text-amber-900 ${txt}`}>It can still go — pick why. Your name and the time are logged with it.{after ? ` ${after}` : ""}</div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {BYPASS_REASONS.map((r) => (
          <button key={r} type="button" data-testid={`no-photo-reason-${slug(r)}`} onClick={() => setReason(r)}
            className={`rounded-full px-4 ${big ? "min-h-[52px] text-lg" : "min-h-[44px] text-sm"} font-semibold border-2 transition-colors ${reason === r ? "bg-[#B01E28] text-white border-[#B01E28]" : "bg-white border-slate-300 hover:border-[#B01E28]"}`}>{r}</button>
        ))}
      </div>
      <Textarea data-testid="no-photo-note" value={note} onChange={(e) => setNote(e.target.value)}
        placeholder={needsNote ? "Say why in a few words (needed for Other)" : "Add a note (optional)"}
        className={`rounded-lg min-h-[64px] ${txt}`} />
      <div className="grid gap-2">
        <button type="button" data-testid="no-photo-confirm" disabled={!ready || busy} onClick={() => onConfirm({ reason, note: note.trim() })}
          className={`w-full rounded-xl bg-[#B01E28] hover:bg-[#8f1820] px-5 font-black text-white disabled:opacity-40 inline-flex items-center justify-center gap-2 transition-colors ${big ? "h-14 text-lg" : "h-12 text-base"}`}>
          {busy && <Loader2 className="w-5 h-5 animate-spin" />}{ready ? actionLabel : "Pick a reason first"}
        </button>
        <button type="button" data-testid="no-photo-cancel" onClick={onCancel} disabled={busy}
          className={`w-full rounded-xl border-2 border-slate-300 bg-white px-4 font-bold text-slate-700 hover:border-slate-500 transition-colors ${big ? "h-14 text-lg" : "h-12 text-base"}`}>Cancel — I'll take the photo</button>
      </div>
    </div>
  );
}

export function NoPhotoBypassDialog({ block, onClose, ...rest }) {
  return (
    <Dialog open={!!block} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-xl sm:max-w-lg [&>button:last-child]:h-11 [&>button:last-child]:w-11 [&>button:last-child]:right-2 [&>button:last-child]:top-2 [&>button:last-child]:flex [&>button:last-child]:items-center [&>button:last-child]:justify-center" data-testid="no-photo-bypass-dialog">
        <DialogTitle className="sr-only">No photo yet</DialogTitle>
        <DialogDescription className="sr-only">Pick a reason to continue without the photo.</DialogDescription>
        {block && <NoPhotoBypassPanel key={block.short_code || "batch"} block={block} onCancel={onClose} {...rest} />}
      </DialogContent>
    </Dialog>
  );
}
